// 장비 장부(D2a·D2b): 가방·장착·강화석·보호 주문서·강화 기록을 하나의 상태로 두고, 모든 변경을 id가 있는 연산으로만 한다.
// 연산 적용은 순수·결정적이다(강화 결과 = 연산 id + 개체 uid 씨앗). 같은 id는 두 번 적용되지 않는다(최근 APPLIED_CAP개 기억) —
// 재시도·재전송·재로드·탭 합치기에서 강화가 두 번 일어나거나 줍기가 두 번 들어가지 않는다. 서버 권위로 옮기면 같은 reducer를 서버가 돈다.
// 저장 형식(dungeonhero.gear.v1)의 직렬화·검증도 여기 둔다. 저장소 입출력은 game/gear.ts.
import { GRADES, PROTECT_WEEKLY, SOCKET_CAP } from './content/items';
import {
  ALCHEMIST_TOWN, ELIXIRS, GEM_CODES, GEM_COMBINE, GEM_TIERS, JEWELER_TOWN, POTIONS, SCROLLS, STACK_CAP, elixirEnd, elixirPrice, gemCombineFee, gemTierOf,
  isGemCode, shopPrice, unsocketFee,
} from './content/consumables';
import { SHOP_PRICE, SHOP_TIER_PRICE_MULT, planStoneConversion, type StoneTier } from './content/economy';
import { APPLIED_CAP, BAG_CAP, CARD_APPLIED_CAP, CARD_APPLIED_PREFIX, ENHANCE_LOG_CAP, MARKS_CAP } from './content/loot';
import { ENHANCE_MAX } from './content/world';
import {
  MASTERY_MILESTONES, TRANSCEND_MAX, TRANSCEND_STEPS, TRANSCEND_STONE_TIER, cloneMastery, freshMastery, isRespec, masteryAllocValid, masteryEmpty, milestoneClaimed,
  parseMastery, respecCost, transcendChance, type MasteryAlloc, type MasterySave,
} from './mastery';
import { townGearBuyableBy, townGearItem, townGearOffer, townGearPrice } from './content/townShop';
import { isLegacyItemId, legacyGearItem, legacyUid } from './content/legacyGear';
import type { EquipSlot, Grade, HeroId } from './content/types';
import {
  applyEnhanceRoll, ensureUnique, equipDef, freshPity, hashSeed, isDownStep, itemSlot, nextEnhanceCost, nextRand, rollEnhance, salvageValue, seedRng,
  type EnhanceOutcome, type EnhanceRoll, type GearAffix, type GearItem,
} from './gear';
import { idHash48 } from './idHash';

/** 읽을 수 있는 가장 높은 저장 버전(v2 = D2c 소비·보석·비약 포함, v3 = 숙련·초월 ms 포함). 이보다 높으면 future */
export const GEAR_VERSION = 3;
/**
 * 연산 기억 압축 저장 버전(D10 저장 크기): applied에 압축 id(접두 + '#' + 48비트 해시)가 있으면 4.
 * 옛 빌드(v3까지)는 압축 id를 원래 id와 맞춰 보지 못해 같은 연산을 다시 적용할 수 있으므로 future로 읽고 쓰지 않는다
 */
export const GEAR_VERSION_COMPACT = 4;
/** applied가 이 수를 넘으면 전부 압축 id로 바꿔 둔다(원래 약 43B → 약 15B). 그 아래는 원래 id 그대로(v3 유지, 롤백 호환) */
export const APPLIED_COMPACT_AT = 128;
/**
 * 영웅별 무기 칸 저장 버전(lyra-weapon-slot): hw(카엘 외 영웅의 무기 칸)가 하나라도 차 있으면 5. 압축 id(v4)도 함께 읽는다.
 * 옛 빌드(v4까지)는 hw를 몰라 라이라 무기를 "가방 장비"로 보고 정리·거래로 지울 수 있으므로 future로 읽고 쓰지 않는다.
 * hw가 비면 예전 규칙 그대로(v1~v4) — 롤백해도 잃을 것이 없다
 */
export const GEAR_VERSION_HW = 5;
/** 자기 무기 칸(hw)을 갖는 영웅. 카엘 무기는 예전처럼 equipped.weapon(옛 저장·옛 빌드와 같은 뜻) */
export const HW_HEROES: readonly HeroId[] = ['lyra', 'seren', 'brann', 'lumen'];
const HW_SET = new Set<string>(HW_HEROES);
const COMPACT_RE = /^(?:[a-z]{1,4}:)?#[A-Za-z0-9_-]{8}$/;
/** 연산 id → 압축 id. 접두(cd:·mk:·dl: 등, 기억 상한 갈래)는 남긴다. 이미 압축 id면 그대로 */
export function compactOpId(id: string): string {
  if (COMPACT_RE.test(id)) return id;
  // 접두는 COMPACT_RE와 같은 모양(소문자 1~4자 + ':')일 때만 남긴다 — 다르면 압축 id가 다시 압축되어 멱등이 깨진다
  const pre = /^[a-z]{1,4}:/.exec(id);
  return (pre ? pre[0] : '') + '#' + idHash48(id);
}
function hasCompact(a: readonly string[]): boolean {
  for (const id of a) if (COMPACT_RE.test(id)) return true;
  return false;
}
/** D2c 내용은 있고 숙련·초월(ms)은 없는 저장 버전 */
export const GEAR_VERSION_D2C = 2;
/** D2c 내용이 없을 때 쓰는 저장 버전. 옛 빌드(v1만 앎)가 읽어도 잃을 것이 없는 경우다 */
export const GEAR_VERSION_BASE = 1;
/** 보석·비약 배열 길이 상한(앞으로 종류가 늘어도 기존 저장을 손상 처리하지 않는다) */
const COUNT_ARRAY_MAX = 64;
export const SLOT_ORDER: readonly EquipSlot[] = ['weapon', 'helm', 'armor', 'gloves', 'boots', 'amulet'];

/** 강화 장부 한 줄(화면 공개·재로드 검증용). op = 연산 id(중복 0의 기준) */
export interface EnhanceLog {
  readonly op: string;
  readonly uid: string;
  readonly from: number;
  readonly to: number;
  readonly outcome: EnhanceOutcome;
  /** 그 시도의 실효 확률(천장 포함) */
  readonly chance: number;
  readonly protect: boolean;
  readonly stones: number;
  readonly gold: number;
}

export interface GearState {
  /** 쓰기마다 1 증가(탭 합치기의 기준: 더 큰 쪽이 새 상태) */
  rev: number;
  /** 마지막으로 쓴 탭 */
  by: string;
  /** 가방 + 장착 장비(장착은 equipped가 uid로 가리킨다) */
  items: GearItem[];
  equipped: Record<EquipSlot, string | null>;
  /**
   * 영웅별 무기 칸(lyra-weapon-slot): 카엘 외 영웅 → 그 영웅이 낀 무기 uid. 방어구·장신구 5부위는 계정 공유(equipped)이고 무기만 영웅별이다
   * (장르 상위 게임의 "영웅 전용 무기 + 공유 장비" 구조). 카엘 무기 = equipped.weapon. 비어 있으면 키가 없다
   */
  hw: Partial<Record<HeroId, string>>;
  /** 강화석 하·중·상·최상 */
  stones: number[];
  protects: number;
  /** 장비 경제 순지출 골드 = 강화·주문서 구매 − 판매. 골드 잔액 = 획득 − 상점 구매 − 이것(ProfileStore) */
  goldNet: number;
  /** 최근 적용한 연산 id(오래된 것부터 버린다) */
  applied: string[];
  /** 최근 강화 기록(오래된 것부터 버린다) */
  log: EnhanceLog[];
  /** 골드 상점 보호 주문서: 마지막으로 산 주 번호와 그 주에 산 수 */
  protectWeek: number;
  protectBought: number;
  /**
   * 탭별 적용 수위표 [탭 nonce, 그 탭이 적용시킨 가장 큰 seq](최근에 쓴 탭부터 MARKS_CAP개). applied 목록이 잘려도(APPLIED_CAP)
   * "목록에서 빠짐"과 "적용 안 됨"을 구분한다: seq ≤ 수위면 이미 적용된 연산이다(c009 교차 리뷰 P2).
   */
  marks: [string, number][];
  /** 소비 아이템 보유(D2c): 물약·비약·주문서 id → 수(STACK_CAP까지). 모르는 id(다음 빌드)도 버리지 않는다 */
  cons: Record<string, number>;
  /** 보석 보유(코드 0..14 → 수, content/consumables gemCode) */
  gems: number[];
  /** 비약 끝 시각(플레이 초, ELIXIRS 순서 4칸, 0 = 없음). 남은 시간 = 끝 − play */
  elix: number[];
  /** 플레이 시계(접속 시간 초). 연산이 아니라 탭 합치기에서 큰 쪽을 쓴다(오프라인 동안 멈춘다, PRD 4.6) */
  play: number;
  /** 숙련 배분·재분배 횟수·부위별 초월 단계·이정표(C4, shared/mastery). 골드·강화석을 쓰는 연산이라 같은 장부에 둔다 */
  ms: MasterySave;
}

export type GearOpBody =
  | { readonly id: string; readonly t: 'pickup'; readonly item: GearItem }
  | { readonly id: string; readonly t: 'stones'; readonly tier: StoneTier; readonly n: number }
  | { readonly id: string; readonly t: 'equip'; readonly uid: string; readonly hero: HeroId }
  /** hero: 무기 칸을 벗을 영웅(없으면 카엘 — 옛 연산·옛 탭과 같은 뜻). 방어구·장신구는 영웅과 상관없다 */
  | { readonly id: string; readonly t: 'unequip'; readonly slot: EquipSlot; readonly hero?: HeroId }
  /** cap: 지금 강화 상한(마을·지역6), region6: 보호 주문서 해금, gold: 명령 순간 쓸 수 있는 골드 */
  | { readonly id: string; readonly t: 'enhance'; readonly uid: string; readonly protect: boolean; readonly cap: number; readonly region6: boolean; readonly gold: number }
  | { readonly id: string; readonly t: 'salvage'; readonly uid: string }
  | { readonly id: string; readonly t: 'buyProtect'; readonly week: number; readonly town: number; readonly region6: boolean; readonly gold: number }
  // ── D2c 소비 아이템·보석 ──
  /** 상점 구매(물약·주문서): town = 도착한 가장 높은 마을(판매 여부·가격), gold = 명령 순간 쓸 수 있는 골드 */
  | { readonly id: string; readonly t: 'buy'; readonly item: string; readonly n: number; readonly town: number; readonly gold: number }
  /** 연금술사 비약 제작(마을④부터) */
  | { readonly id: string; readonly t: 'craft'; readonly item: string; readonly n: number; readonly town: number; readonly gold: number }
  /** 물약·주문서 1개 사용(던전 고정 스텝에서도 — 연산 id는 판 nonce + 순번) */
  | { readonly id: string; readonly t: 'use'; readonly item: string }
  /** 비약 1병 마시기: at = 마신 순간 플레이 시계(초). 같은 연산을 다시 적용해도 같은 끝 시각 */
  | { readonly id: string; readonly t: 'elixir'; readonly item: string; readonly at: number }
  /** 보석 줍기(드롭) */
  | { readonly id: string; readonly t: 'gems'; readonly code: number; readonly n: number }
  /** 보석 박기(무료): uid 장비의 idx번 빈 소켓에 code 보석 1개 */
  | { readonly id: string; readonly t: 'socket'; readonly uid: string; readonly idx: number; readonly code: number; readonly town: number }
  /** 보석 빼기(수수료, 보석은 돌려받는다) */
  | { readonly id: string; readonly t: 'unsocket'; readonly uid: string; readonly idx: number; readonly town: number; readonly gold: number }
  /** 보석 합성: 같은 보석 3개 → 한 등급 위 1개(확정) */
  | { readonly id: string; readonly t: 'combine'; readonly code: number; readonly town: number; readonly gold: number }
  // ── S7 마을 장비 상점 ──
  /**
   * 장비 구매: offer = 품목 id(content/townShop), town = 지금 머문 마을(품목은 그 마을 것만), hero = 지금 영웅(무기 정의),
   * gold = 명령 순간 쓸 수 있는 골드. 개체는 장부가 품목 id와 연산 id로 다시 만든다(보낸 개체를 믿지 않는다)
   */
  | { readonly id: string; readonly t: 'buyGear'; readonly offer: string; readonly town: number; readonly hero: HeroId; readonly gold: number }
  // ── 장비 통합(ECONOMY 14절) ──
  /**
   * 옛 상점 장비 전환: add = 넣을 옛 id(content/legacyGear), equip = 넣고 장착할 옛 id(그 부위가 비었을 때만).
   * 개체는 장부가 옛 id로 다시 만든다(보낸 개체를 믿지 않는다). 같은 uid가 있으면 건너뛴다. 가방 상한을 넘어도 넣는다(전환으로 장비가 정리되지 않게)
   */
  | { readonly id: string; readonly t: 'legacy'; readonly add: readonly string[]; readonly equip: readonly string[] }
  // ── 레이드 보상(RAID-PRD 6절) ──
  /** 보호 주문서 지급(레이드 주 첫 클리어, 지역6 클리어 뒤만 — 그 전에는 강화석으로 준다). 연산 id 고정이라 한 번만 들어간다 */
  | { readonly id: string; readonly t: 'protect'; readonly n: number }
  // ── 던전 보상 카드(결과 화면) ──
  /** 소비 아이템(물약·주문서) 지급: 보상 카드. 연산 id 고정이라 한 번만 들어간다. 보유 상한(STACK_CAP)을 넘는 몫은 버린다 */
  | { readonly id: string; readonly t: 'grant'; readonly item: string; readonly n: number }
  // ── 우편·거래소(PARTY-TRADE-PRD 1장). 연산 id는 서버 거래 id에서 결정적으로(mk:<idem>, mk:m<우편 id>) ──
  /** 우편으로 받은 장비 넣기: 강화·천장·소켓을 보존한다(거래된 장비의 가치). 같은 uid가 있으면 중복, 가방이 차면 거부(받기를 미룬다) */
  | { readonly id: string; readonly t: 'mkIn'; readonly item: GearItem }
  /** 거래 금고 입출금: d > 0 수입(출금·판매 대금·파티 보상), d < 0 지출(맡기기). gold = 명령 순간 쓸 수 있는 골드(지출 검사) */
  | { readonly id: string; readonly t: 'mkGold'; readonly d: number; readonly gold?: number }
  /** 맡긴 장비를 가방에서 빼기: 정리와 달리 강화석·골드가 생기지 않고 박힌 보석도 함께 나간다 */
  | { readonly id: string; readonly t: 'mkOut'; readonly uid: string }
  // ── 숙련·초월(C4, PROGRESSION-720H 4·6절) ──
  /**
   * 숙련 배분을 a로 맞춘다(멱등). points = 명령 순간 숙련 포인트(성장 원장에서 계산), gold = 쓸 수 있는 골드.
   * 한 칸이라도 줄면 재분배라 respecCost(횟수) 골드를 내고 횟수가 오른다. 늘리기만 하면 무료
   */
  | { readonly id: string; readonly t: 'msAlloc'; readonly a: MasteryAlloc; readonly points: number; readonly gold: number }
  /** 초월 강화 1회: slot 부위(장착 장비 +15 필요), unlock = 지금 열린 최대 단계(환생 회차·숙련), gold = 쓸 수 있는 골드 */
  | { readonly id: string; readonly t: 'transcend'; readonly slot: EquipSlot; readonly unlock: number; readonly gold: number; readonly hero?: HeroId }
  /** 숙련 이정표 보상 받기: idx = MASTERY_MILESTONES 인덱스, level = 명령 순간 숙련 레벨 */
  | { readonly id: string; readonly t: 'msClaim'; readonly idx: number; readonly level: number };
/** src·seq: 연산을 만든 탭 nonce와 그 탭 안의 단조 순번(저장 계층이 붙인다). 둘 다 있으면 수위표로 중복을 판정한다 */
export type GearOp = GearOpBody & { readonly src?: string; readonly seq?: number };

export type GearCode =
  | 'ok'
  | 'duplicate' // 이미 적용한 연산 id(같은 결과를 다시 내지 않는다)
  | 'auto_salvaged' // 가방이 가득 차 줍자마자 정리했다
  | 'bag_full' // 가방이 가득 차 벗을 수 없다
  | 'unknown_item'
  | 'equipped' // 장착 중이라 정리할 수 없다
  | 'wrong_hero' // 다른 영웅 전용 무기
  | 'max' // 이미 +15
  | 'capped' // 지금 강화 상한(마을·지역6 클리어)
  | 'no_stones'
  | 'no_gold'
  | 'no_protect' // 보호 주문서를 쓰려 했는데 없음
  | 'protect_locked' // 지역6 클리어 전(보호 주문서 미해금)
  | 'limit' // 주간 구매 한도
  | 'readonly' // 저장 손상·미래 버전·저장소 없음, 다른 탭이 거래 권한
  | 'save_failed'
  | 'locked' // 시설·품목이 아직 열리지 않음(마을 단계)
  | 'no_item' // 쓸 소비 아이템·보석이 없음
  | 'bad_socket' // 없는 소켓 칸·이미 찬 칸·빈 칸
  | 'need15' // 초월: 그 부위 장비가 +15가 아니다
  | 'denied'; // 문맥(마을 밖·창 닫힘·일시정지)

export interface ApplyResult {
  code: GearCode;
  roll?: EnhanceRoll;
  /** 초월 판정(공개: 실효 확률·성공 여부·단계) */
  tr?: { readonly slot: EquipSlot; readonly from: number; readonly to: number; readonly chance: number; readonly ok: boolean };
  /** 이번 연산의 골드 변화(+ 지출, − 수입)와 강화석 변화 */
  gold?: number;
  stones?: number;
  tier?: StoneTier;
}

export function freshGear(): GearState {
  return {
    rev: 0, by: '', items: [], equipped: { weapon: null, helm: null, armor: null, gloves: null, boots: null, amulet: null }, hw: {},
    stones: [0, 0, 0, 0], protects: 0, goldNet: 0, applied: [], log: [], protectWeek: -1, protectBought: 0, marks: [],
    cons: {}, gems: new Array<number>(GEM_CODES).fill(0), elix: new Array<number>(ELIXIRS.length).fill(0), play: 0, ms: freshMastery(),
  };
}

/** 깊은 복사(연산을 시험 적용할 때). 개체의 affixes는 바뀌지 않으므로 공유한다 */
export function cloneGear(s: GearState): GearState {
  return {
    ...s, items: s.items.map((it) => (it.sock ? { ...it, pity: it.pity.slice(), sock: it.sock.slice() } : { ...it, pity: it.pity.slice() })), equipped: { ...s.equipped }, hw: { ...s.hw }, stones: s.stones.slice(),
    applied: s.applied.slice(), log: s.log.slice(), marks: s.marks.map((m) => [m[0], m[1]] as [string, number]),
    cons: { ...s.cons }, gems: s.gems.slice(), elix: s.elix.slice(), ms: cloneMastery(s.ms),
  };
}

export function findItem(s: GearState, uid: string): GearItem | undefined {
  for (const it of s.items) if (it.uid === uid) return it;
  return undefined;
}
export function isEquipped(s: GearState, uid: string): boolean {
  for (const k of SLOT_ORDER) if (s.equipped[k] === uid) return true;
  for (const h of HW_HEROES) if (s.hw[h] === uid) return true;
  return false;
}
/** 그 영웅의 무기 칸 uid(카엘 = equipped.weapon, 다른 영웅 = hw). 영웅 미지정 = 카엘 */
export function weaponUidOf(s: Pick<GearState, 'equipped' | 'hw'>, hero: HeroId | undefined): string | null {
  return !hero || hero === 'kael' ? s.equipped.weapon : s.hw[hero] ?? null;
}
/** 그 영웅이 보는 부위 uid(무기만 영웅별) */
export function slotUidOf(s: Pick<GearState, 'equipped' | 'hw'>, slot: EquipSlot, hero: HeroId | undefined): string | null {
  return slot === 'weapon' ? weaponUidOf(s, hero) : s.equipped[slot];
}
/** uid를 모든 무기 칸에서 뺀다(전용 없는 무기를 다른 영웅이 끼면 옮겨 간다 — 한 개체가 두 칸에 있지 않게) */
function clearWeapon(s: GearState, uid: string): void {
  if (s.equipped.weapon === uid) s.equipped.weapon = null;
  for (const h of HW_HEROES) if (s.hw[h] === uid) delete s.hw[h];
}
function hwEmpty(s: GearState): boolean {
  for (const h of HW_HEROES) if (s.hw[h]) return false;
  return true;
}
/** 가방(장착하지 않은) 장비 수 */
export function bagCount(s: GearState): number {
  let n = 0;
  for (const it of s.items) if (!isEquipped(s, it.uid)) n++;
  return n;
}
/** 그 영웅이 낀 6부위(무기 = 그 영웅 칸). 영웅 미지정 = 카엘(옛 호출과 같다) */
export function equippedItems(s: GearState, out: (GearItem | null)[] = [], hero?: HeroId): (GearItem | null)[] {
  out.length = 0;
  for (const k of SLOT_ORDER) { const u = slotUidOf(s, k, hero); out.push(u ? findItem(s, u) ?? null : null); }
  return out;
}
/** 연산 id가 기억에 있는지: 원래 id 또는 압축 id(D10) */
export function hasApplied(s: GearState, id: string): boolean {
  return s.applied.includes(id) || (s.applied.length > 0 && s.applied.includes(compactOpId(id)));
}
/** 탭 src의 수위(그 탭 연산 중 이미 적용된 가장 큰 seq, 없으면 0) */
export function markOf(s: GearState, src: string): number {
  for (const m of s.marks) if (m[0] === src) return m[1];
  return 0;
}
/** 이 연산이 이미 적용됐는지: id가 최근 목록에 있거나, 만든 탭의 수위 이하 */
export function isApplied(s: GearState, op: GearOp): boolean {
  if (hasApplied(s, op.id)) return true;
  return !!op.src && typeof op.seq === 'number' && op.seq > 0 && markOf(s, op.src) >= op.seq;
}
function setMark(s: GearState, src: string, seq: number): void {
  const m = s.marks;
  let i = -1;
  for (let k = 0; k < m.length; k++) if (m[k][0] === src) { i = k; break; }
  if (i >= 0) {
    // 이미 있는 탭: 그 칸을 맨 앞으로 옮기고 값만 올린다(줍기마다 새 배열을 만들지 않는다, c015)
    const row = m[i];
    for (let k = i; k > 0; k--) m[k] = m[k - 1];
    m[0] = row;
    row[1] = Math.max(row[1], seq);
    return;
  }
  m.unshift([src, seq]);
  if (m.length > MARKS_CAP) m.length = MARKS_CAP;
}

/**
 * 연산 기억 정리: 보상 카드 지급(`cd:<영수증 id>`)은 다른 연산이 아무리 많이 지나가도 지워지지 않게 따로 센다(CARD_APPLIED_CAP개까지).
 * 탭이 닫혀 영수증에 지급 완료(cardGranted)를 못 적은 채 장부 연산이 APPLIED_CAP개를 넘겨도 같은 카드를 다시 지급하지 않기 위해서다(P3-1).
 * 일반 연산은 예전처럼 최근 APPLIED_CAP개만 남긴다. 순서는 그대로 둔다.
 */
export function trimApplied(a: string[]): void {
  // 기억이 길어지면 압축 id로 바꿔 둔다(순서·접두 유지 — 아래 상한 갈래 계산은 같다)
  if (a.length > APPLIED_COMPACT_AT) for (let i = 0; i < a.length; i++) a[i] = compactOpId(a[i]);
  if (a.length <= APPLIED_CAP) return;
  // 오래 기억할 연산 세 갈래: 보상 카드(cd:), 서버 우편·거래소(mk: — 우편 ack가 끊겨 같은 우편을 다시 받아도 두 번 들어가지 않게, PR #243 리뷰),
  // 일일 지급(dl:). 일일 지급이 우편 기억을 밀어내지 않게 갈래를 나눈다(PR #291 Codex 보안 P2 — 900 dl + 300 mk가 같은 1024칸을 쓰면 오래된 우편 id가 빠졌다)
  let cards = 0, market = 0, daily = 0;
  for (let i = 0; i < a.length; i++) {
    const id = a[i];
    if (id.startsWith(CARD_APPLIED_PREFIX)) cards++; else if (id.startsWith(MARKET_APPLIED_PREFIX)) market++; else if (id.startsWith(DAILY_APPLIED_PREFIX)) daily++;
  }
  let dropCard = Math.max(0, cards - CARD_APPLIED_CAP), dropMarket = Math.max(0, market - MARKET_APPLIED_CAP), dropDaily = Math.max(0, daily - DAILY_GEAR_APPLIED_CAP);
  let dropOther = Math.max(0, a.length - cards - market - daily - APPLIED_CAP);
  if (dropCard === 0 && dropMarket === 0 && dropDaily === 0 && dropOther === 0) return;
  let w = 0;
  for (let i = 0; i < a.length; i++) {
    const id = a[i];
    if (id.startsWith(CARD_APPLIED_PREFIX)) { if (dropCard > 0) { dropCard--; continue; } }
    else if (id.startsWith(MARKET_APPLIED_PREFIX)) { if (dropMarket > 0) { dropMarket--; continue; } }
    else if (id.startsWith(DAILY_APPLIED_PREFIX)) { if (dropDaily > 0) { dropDaily--; continue; } }
    else if (dropOther > 0) { dropOther--; continue; }
    a[w++] = id;
  }
  a.length = w;
}
/** 서버 우편·거래소 연산 접두(mk:)와 따로 기억하는 개수 */
export const MARKET_APPLIED_PREFIX = 'mk:';
// 우편은 ack 전까지 서버가 다시 줄 수 있어 오래 기억한다(PR #243 Codex P2 — 다른 우편 수백 통 뒤 재수령 이중 지급 방지).
// 1024(D10 저장 크기, 2026-10-30 4096에서 줄임): 압축 id 약 15B × 1024 ≈ 15KB. 일일 지급(dl:)은 따로 센다(DAILY_GEAR_APPLIED_CAP) —
// 우편·거래만 1024칸이라 720h 우편·거래(약 300건)는 하나도 빠지지 않고, ack를 막은 채 우편을 다시 받으려면 그 뒤 우편·거래 1,024건이 더 필요하다
export const MARKET_APPLIED_CAP = 1024;
/** 일일 콘텐츠 지급(dl:, L9 — src/shared/daily.ts Payout): 우편과 같은 칸에서 오래 기억한다(일일 장부의 지급 표시가 늦게 합쳐져도 강화석·주문서가 두 번 들어가지 않게) */
export const DAILY_APPLIED_PREFIX = 'dl:';
/**
 * 일일 지급(dl:) 기억 수(D10에서 우편과 갈래를 나눔). 일일 장부가 지급 끝난 건을 최근 PAYOUT_PAID_CAP(64)개 들고 있고 골드는 save.v1 해시가 막으므로
 * 그보다 넉넉한 256개(하루 10건 안팎 × 약 25일)면 늦게 합쳐진 일일 장부의 재지급을 막는다
 */
export const DAILY_GEAR_APPLIED_CAP = 256;
function remember(s: GearState, id: string): void {
  s.applied.push(id);
  trimApplied(s.applied);
}
function salvageInto(s: GearState, it: GearItem): ApplyResult {
  const v = salvageValue(it);
  s.goldNet -= v.gold;
  s.stones[v.tier] += v.stones;
  return { code: 'ok', gold: -v.gold, stones: v.stones, tier: v.tier };
}

/** 자주 나는 결과는 공용 객체(읽기 전용으로 쓴다). 강화석 결과는 다음 호출 전까지만 유효하다 */
const RES_OK: ApplyResult = Object.freeze({ code: 'ok' }) as ApplyResult;
const RES_DUPLICATE: ApplyResult = Object.freeze({ code: 'duplicate' }) as ApplyResult;
const STONES_RES: ApplyResult = { code: 'ok', stones: 0, tier: 0 };

/**
 * 연산 하나를 상태에 적용한다(s를 바꾼다). 결과 코드가 ok·auto_salvaged일 때만 상태가 바뀌고 연산 id를 기억한다.
 * 같은 id가 이미 있으면 duplicate로 아무것도 바꾸지 않는다. rev·by는 저장 계층이 올린다.
 */
export function applyGearOp(s: GearState, op: GearOp): ApplyResult {
  if (typeof op.id !== 'string' || op.id.length === 0 || op.id.length > 160) return { code: 'denied' };
  if (isApplied(s, op)) return RES_DUPLICATE;
  let res: ApplyResult;
  switch (op.t) {
    case 'pickup': {
      // 같은 개체를 두 번 넣지 않는다(연산 id 창 밖의 재전송 방어)
      if (findItem(s, op.item.uid)) return RES_DUPLICATE;
      // 저장 상태에 들어가는 개체 사본(강화가 제자리에서 바꾸므로 연산의 개체와 나눠 둔다). 결과 객체는 공용 상수(c015 줍기 할당 줄임)
      const item: GearItem = ensureUnique({ ...op.item, enh: 0, pity: freshPity() });
      // 이름 있는 유니크는 가방이 가득해도 자동 정리하지 않고 넣는다(희귀 드롭을 잃지 않게, BALANCE-1028). 다음 줍기는 그대로 정리된다
      if (bagCount(s) >= BAG_CAP && item.nu === undefined) { res = salvageInto(s, item); res.code = 'auto_salvaged'; }
      else { s.items.push(item); res = RES_OK; }
      break;
    }
    case 'stones': {
      if (!(op.tier >= 0 && op.tier <= 3) || !Number.isSafeInteger(op.n) || op.n <= 0) return { code: 'denied' };
      s.stones[op.tier] += op.n;
      // 강화석 줍기는 가장 잦은 연산이라 결과를 재사용한다(다음 applyGearOp 호출 전까지만 유효)
      res = STONES_RES; res.stones = op.n; res.tier = op.tier;
      break;
    }
    case 'protect': {
      if (!Number.isSafeInteger(op.n) || op.n <= 0 || op.n > 9) return { code: 'denied' };
      s.protects += op.n;
      res = { code: 'ok' };
      break;
    }
    case 'grant': {
      if (!POTIONS.some((p) => p.id === op.item) && !SCROLLS.some((x) => x.id === op.item)) return { code: 'denied' };
      if (!Number.isSafeInteger(op.n) || op.n < 1 || op.n > 99) return { code: 'denied' };
      s.cons[op.item] = Math.min(STACK_CAP, (s.cons[op.item] ?? 0) + op.n);
      res = RES_OK;
      break;
    }
    case 'legacy': {
      let n = 0;
      for (const id of op.add) {
        if (!isLegacyItemId(id) || findItem(s, legacyUid(id))) continue;
        s.items.push(legacyGearItem(id)); n++;
      }
      for (const id of op.equip) {
        if (!isLegacyItemId(id)) continue;
        const it = findItem(s, legacyUid(id)), d = it ? equipDef(it.def) : undefined;
        if (!it || !d || s.equipped[d.slot]) continue;
        s.equipped[d.slot] = it.uid; n++;
      }
      if (n === 0) return RES_DUPLICATE;
      res = { code: 'ok' };
      break;
    }
    case 'equip': {
      const it = findItem(s, op.uid);
      const d = it ? equipDef(it.def) : undefined;
      if (!it || !d) return { code: 'unknown_item' };
      if (d.heroLock && d.heroLock !== op.hero) return { code: 'wrong_hero' };
      if (d.slot === 'weapon') {
        // 무기는 영웅별 칸(lyra-weapon-slot). 다른 칸에 있던 같은 개체는 옮겨 온다
        clearWeapon(s, it.uid);
        if (op.hero === 'kael' || !HW_SET.has(op.hero)) s.equipped.weapon = it.uid;
        else s.hw[op.hero] = it.uid;
      } else s.equipped[d.slot] = it.uid;
      res = { code: 'ok' };
      break;
    }
    case 'unequip': {
      if (!SLOT_ORDER.includes(op.slot)) return { code: 'denied' };
      // 벗은 장비는 가방으로 간다: 가방이 가득 차 있으면 거절(61/60이 되지 않게, PR #154 P2)
      const wHero = op.slot === 'weapon' && op.hero && HW_SET.has(op.hero) ? op.hero : null;
      if ((wHero ? s.hw[wHero] : s.equipped[op.slot]) && bagCount(s) >= BAG_CAP) return { code: 'bag_full' };
      if (wHero) delete s.hw[wHero];
      else s.equipped[op.slot] = null;
      res = { code: 'ok' };
      break;
    }
    case 'salvage': {
      const it = findItem(s, op.uid);
      if (!it) return { code: 'unknown_item' };
      if (isEquipped(s, it.uid)) return { code: 'equipped' };
      s.items.splice(s.items.indexOf(it), 1);
      // 박힌 보석은 정리하면서 보석함으로 돌려준다(D2c: 정리로 보석을 잃지 않는다, 탈착 수수료 없음)
      if (it.sock) for (const c of it.sock) if (isGemCode(c)) s.gems[c]++;
      res = salvageInto(s, it);
      break;
    }
    case 'enhance': {
      const it = findItem(s, op.uid);
      if (!it || !equipDef(it.def)) return { code: 'unknown_item' };
      if (it.enh >= ENHANCE_MAX) return { code: 'max' };
      if (it.enh + 1 > op.cap) return { code: 'capped' };
      const cost = nextEnhanceCost(it);
      const down = isDownStep(it);
      const protect = op.protect && down;
      if (protect && !op.region6) return { code: 'protect_locked' };
      if (protect && s.protects < 1) return { code: 'no_protect' };
      // 강화석이 모자라면 아래 등급을 5:1로 변환해 채운다(대장간, 중간 등급 보유분을 합쳐 쓰고 단계마다 변환 수수료 — 시뮬과 같은 함수)
      const conv = planStoneConversion(s.stones, cost.tier, Math.max(0, cost.stones - s.stones[cost.tier]));
      if (!conv.ok) return { code: 'no_stones' };
      if (op.gold < cost.gold + conv.fee) return { code: 'no_gold' };
      for (let k = 0; k < cost.tier; k++) s.stones[k] -= conv.take[k];
      s.stones[cost.tier] += conv.made - cost.stones;
      s.goldNet += cost.gold + conv.fee;
      const roll = rollEnhance(it, hashSeed(op.id + '|' + it.uid), protect);
      applyEnhanceRoll(it, roll);
      if (roll.outcome === 'protected') s.protects--;
      s.log.push({ op: op.id, uid: it.uid, from: roll.from, to: roll.to, outcome: roll.outcome, chance: Math.round(roll.chance * 10000) / 10000, protect, stones: cost.stones, gold: cost.gold + conv.fee });
      if (s.log.length > ENHANCE_LOG_CAP) s.log.splice(0, s.log.length - ENHANCE_LOG_CAP);
      res = { code: 'ok', roll, gold: cost.gold + conv.fee, stones: -cost.stones, tier: cost.tier };
      break;
    }
    case 'buyProtect': {
      if (!op.region6) return { code: 'protect_locked' };
      const bought = s.protectWeek === op.week ? s.protectBought : 0;
      if (bought >= PROTECT_WEEKLY.goldShop) return { code: 'limit' };
      const price = protectPrice(op.town);
      if (op.gold < price) return { code: 'no_gold' };
      s.goldNet += price;
      s.protects++;
      s.protectWeek = op.week; s.protectBought = bought + 1;
      res = { code: 'ok', gold: price };
      break;
    }
    case 'buy': case 'craft': {
      if (!Number.isSafeInteger(op.n) || op.n < 1 || op.n > STACK_CAP) return { code: 'denied' };
      let unit: number | null;
      if (op.t === 'buy') unit = shopPrice(op.item, op.town);
      else unit = ELIXIRS.some((e) => e.id === op.item) && op.town >= ALCHEMIST_TOWN ? elixirPrice(op.town) : null;
      if (unit === null || !Number.isSafeInteger(unit) || unit <= 0) return { code: 'locked' }; // NaN·음수·비정수 가격은 거래 불가
      if ((s.cons[op.item] ?? 0) + op.n > STACK_CAP) return { code: 'limit' };
      const cost = unit * op.n;
      if (!(op.gold >= cost)) return { code: 'no_gold' };
      s.goldNet += cost;
      s.cons[op.item] = (s.cons[op.item] ?? 0) + op.n;
      res = { code: 'ok', gold: cost };
      break;
    }
    case 'use': {
      if (!POTIONS.some((p) => p.id === op.item) && !SCROLLS.some((x) => x.id === op.item)) return { code: 'denied' };
      const have = s.cons[op.item] ?? 0;
      if (have < 1) return { code: 'no_item' };
      s.cons[op.item] = have - 1;
      res = RES_OK;
      break;
    }
    case 'elixir': {
      const k = ELIXIRS.findIndex((e) => e.id === op.item);
      if (k < 0 || !Number.isSafeInteger(op.at) || op.at < 0) return { code: 'denied' };
      const have = s.cons[op.item] ?? 0;
      if (have < 1) return { code: 'no_item' };
      s.cons[op.item] = have - 1;
      s.elix[k] = elixirEnd(s.elix[k], op.at, ELIXIRS[k].durationSec);
      res = RES_OK;
      break;
    }
    case 'gems': {
      if (!isGemCode(op.code) || !Number.isSafeInteger(op.n) || op.n <= 0) return { code: 'denied' };
      s.gems[op.code] += op.n;
      res = RES_OK;
      break;
    }
    case 'socket': case 'unsocket': {
      if (op.town < JEWELER_TOWN) return { code: 'locked' };
      const it = findItem(s, op.uid);
      if (!it || !equipDef(it.def)) return { code: 'unknown_item' };
      const n = SOCKET_CAP[it.grade];
      if (!Number.isInteger(op.idx) || op.idx < 0 || op.idx >= n) return { code: 'bad_socket' };
      const sock = socketRow(it, n);
      if (op.t === 'socket') {
        if (!isGemCode(op.code)) return { code: 'denied' };
        if (sock[op.idx] >= 0) return { code: 'bad_socket' };
        if (s.gems[op.code] < 1) return { code: 'no_item' };
        s.gems[op.code]--;
        sock[op.idx] = op.code;
        it.sock = sock;
        res = RES_OK;
      } else {
        const c = sock[op.idx];
        if (!isGemCode(c)) return { code: 'bad_socket' }; // 빈 칸이거나 모르는 코드(다음 빌드 보석)는 뺄 수 없다
        const fee = unsocketFee(op.town);
        if (!Number.isSafeInteger(fee) || fee < 0) return { code: 'denied' };
        if (!(op.gold >= fee)) return { code: 'no_gold' };
        s.goldNet += fee;
        s.gems[c]++;
        sock[op.idx] = -1;
        it.sock = sock;
        res = { code: 'ok', gold: fee };
      }
      break;
    }
    case 'combine': {
      if (op.town < JEWELER_TOWN) return { code: 'locked' };
      if (!isGemCode(op.code) || gemTierOf(op.code) >= GEM_TIERS) return { code: 'denied' };
      if (s.gems[op.code] < GEM_COMBINE.need) return { code: 'no_item' };
      const fee = gemCombineFee(op.code, op.town);
      if (!Number.isSafeInteger(fee) || fee < 0) return { code: 'denied' };
      if (!(op.gold >= fee)) return { code: 'no_gold' };
      s.goldNet += fee;
      s.gems[op.code] -= GEM_COMBINE.need;
      s.gems[op.code + 1]++;
      res = { code: 'ok', gold: fee };
      break;
    }
    case 'mkIn': {
      const item = mailItemCopy(op.item);
      if (!item) return { code: 'denied' };
      if (findItem(s, item.uid)) return RES_DUPLICATE;
      if (bagCount(s) >= BAG_CAP) return { code: 'bag_full' };
      s.items.push(item);
      res = RES_OK;
      break;
    }
    case 'mkGold': {
      if (!Number.isSafeInteger(op.d) || op.d === 0 || Math.abs(op.d) > MK_GOLD_MAX) return { code: 'denied' };
      if (op.d > 0) { s.goldNet -= op.d; res = { code: 'ok', gold: -op.d }; }
      else {
        if (!((op.gold ?? 0) >= -op.d)) return { code: 'no_gold' };
        s.goldNet += -op.d;
        res = { code: 'ok', gold: -op.d };
      }
      break;
    }
    case 'msAlloc': {
      if (!masteryAllocValid(op.a, op.points)) return { code: 'denied' };
      const cur = s.ms.a;
      const cost = isRespec(cur, op.a) ? respecCost(s.ms.r) : 0;
      if (cost > 0 && !(op.gold >= cost)) return { code: 'no_gold' };
      if (cost > 0) { s.goldNet += cost; s.ms.r++; }
      s.ms.a = [op.a[0], op.a[1], op.a[2], op.a[3]];
      res = { code: 'ok', gold: cost };
      break;
    }
    case 'transcend': {
      const si = SLOT_ORDER.indexOf(op.slot);
      if (si < 0) return { code: 'denied' };
      // 무기 초월은 그 영웅 무기 칸의 장비로 판정한다(초월 단계 자체는 부위별 — 영웅이 바뀌어도 무기 칸 초월은 그대로)
      const uid = slotUidOf(s, op.slot, op.hero);
      const it = uid ? findItem(s, uid) : undefined;
      if (!it) return { code: 'unknown_item' };
      if (it.enh < ENHANCE_MAX) return { code: 'need15' };
      const from = s.ms.tr[si];
      if (from >= TRANSCEND_MAX) return { code: 'max' };
      if (!Number.isSafeInteger(op.unlock) || from >= Math.min(TRANSCEND_MAX, op.unlock)) return { code: 'capped' };
      const step = TRANSCEND_STEPS[from];
      const conv = planStoneConversion(s.stones, TRANSCEND_STONE_TIER, Math.max(0, step.stones - s.stones[TRANSCEND_STONE_TIER]));
      if (!conv.ok) return { code: 'no_stones' };
      if (!(op.gold >= step.gold + conv.fee)) return { code: 'no_gold' };
      for (let k = 0; k < TRANSCEND_STONE_TIER; k++) s.stones[k] -= conv.take[k];
      s.stones[TRANSCEND_STONE_TIER] += conv.made - step.stones;
      s.goldNet += step.gold + conv.fee;
      // 판정 씨앗 = 연산 id + 부위 + 단계(강화와 같은 결정적 판정: 재적용·탭 합치기에서 결과가 같다)
      const c = transcendChance(from, s.ms.tf[si]);
      const ok = nextRand(seedRng(hashSeed(op.id + '|tr|' + op.slot + '|' + from))) < c.chance;
      if (ok) { s.ms.tr[si] = from + 1; s.ms.tf[si] = 0; } else s.ms.tf[si]++;
      res = { code: 'ok', gold: step.gold + conv.fee, stones: -step.stones, tier: TRANSCEND_STONE_TIER, tr: { slot: op.slot, from, to: s.ms.tr[si], chance: Math.round(c.chance * 10000) / 10000, ok } };
      break;
    }
    case 'msClaim': {
      const m = MASTERY_MILESTONES[op.idx];
      if (!Number.isSafeInteger(op.idx) || !m) return { code: 'denied' };
      if (milestoneClaimed(s.ms.cl, op.idx)) return RES_DUPLICATE;
      if (!(op.level >= m.at)) return { code: 'locked' };
      s.ms.cl += 2 ** op.idx;
      s.stones[TRANSCEND_STONE_TIER] += m.stones;
      s.protects += m.protects;
      s.goldNet -= m.gold;
      res = { code: 'ok', gold: -m.gold, stones: m.stones, tier: TRANSCEND_STONE_TIER };
      break;
    }
    case 'mkOut': {
      const it = findItem(s, op.uid);
      if (!it) return { code: 'unknown_item' };
      if (isEquipped(s, it.uid)) return { code: 'equipped' };
      s.items.splice(s.items.indexOf(it), 1);
      res = RES_OK;
      break;
    }
    case 'buyGear': {
      const o = townGearOffer(op.offer);
      if (!o || o.town !== op.town) return { code: 'locked' };
      // 장착할 수 없는 영웅의 무기는 팔지 않는다(영웅별 무기 칸이 생기기 전까지 — UI를 우회해도 돈이 나가지 않게)
      if (!townGearBuyableBy(o, op.hero)) return { code: 'wrong_hero' };
      const price = townGearPrice(o);
      if (!Number.isSafeInteger(price) || price <= 0) return { code: 'locked' };
      // uid는 연산 id에서 결정적으로(같은 연산을 다시 적용해도 같은 개체, 64비트 해시 두 개)
      const uid = 'shop:' + o.id + ':' + hashSeed(op.id).toString(36) + hashSeed(op.id + '#').toString(36);
      if (findItem(s, uid)) return RES_DUPLICATE;
      if (bagCount(s) >= BAG_CAP) return { code: 'bag_full' };
      if (!(op.gold >= price)) return { code: 'no_gold' };
      s.goldNet += price;
      s.items.push(townGearItem(o, op.hero, uid));
      res = { code: 'ok', gold: price };
      break;
    }
    default: return { code: 'denied' };
  }
  remember(s, op.id);
  if (op.src && typeof op.seq === 'number' && op.seq > 0) setMark(s, op.src, op.seq);
  return res;
}

/** 금고 입출금 한 번의 상한(|d|) */
export const MK_GOLD_MAX = 1_000_000_000;

/**
 * 우편 장비 개체 검사 + 저장 사본(틀리면 null). 서버 우편은 GearItem JSON 그대로(affixes = {id, v} 객체)다.
 * 정의가 이 빌드에 있어야 하고(모르는 정의는 받지 않는다 — 다음 빌드에서 받는다), 등급·아이템 레벨·강화 단계·옵션 모양을 본다.
 * 천장 카운터가 틀린 모양이면 새로 시작하고(가치에 영향 없음), 소켓은 등급 소켓 수 안에서만 보존한다.
 */
export function mailItemCopy(v: unknown): GearItem | null {
  if (!rec(v) || !str(v.uid, 128) || !str(v.def) || !equipDef(v.def) || !GRADE_IDS.has(v.grade as string) || !int(v.ilvl, 1, 999) ||
    !int(v.enh, 0, ENHANCE_MAX) || !Array.isArray(v.affixes) || v.affixes.length > 8) return null;
  const affixes: GearAffix[] = [];
  for (const a of v.affixes) {
    if (!rec(a) || !str(a.id, 40) || typeof a.v !== 'number' || !Number.isFinite(a.v)) return null;
    affixes.push({ id: a.id as GearAffix['id'], v: a.v });
  }
  if (v.uniq !== undefined && !str(v.uniq, 40)) return null;
  // 이름 있는 유니크 표시(레인 B GearItem.nu)는 그대로 보존 — 빠지면 받은 유니크가 효과·거래 가능을 잃는다
  if (v.nu !== undefined && !str(v.nu, 40)) return null;
  const pity = Array.isArray(v.pity) && v.pity.length === ENHANCE_MAX + 1 && v.pity.every((x) => int(x, 0, 100000)) ? (v.pity as number[]).slice() : freshPity();
  const grade = v.grade as Grade;
  let sock: number[] | undefined;
  if (v.sock !== undefined) {
    if (!Array.isArray(v.sock) || v.sock.length > SOCKET_CAP[grade] || !v.sock.every((c) => int(c, -1, 9999))) return null;
    sock = (v.sock as number[]).slice();
  }
  return ensureUnique({ uid: v.uid, def: v.def, grade, ilvl: v.ilvl, enh: v.enh, pity, affixes, ...(typeof v.uniq === 'string' ? { uniq: v.uniq } : {}), ...(typeof v.nu === 'string' ? { nu: v.nu } : {}), ...(sock ? { sock } : {}) });
}

/** 소켓 칸 새 배열(빈 칸 −1, 길이 n = 등급 소켓 수). 기존 값은 앞에서부터 옮긴다(연산은 개체 배열을 제자리에서 바꾸지 않는다) */
function socketRow(it: GearItem, n: number): number[] {
  const out = new Array<number>(n).fill(-1);
  if (it.sock) for (let k = 0; k < n && k < it.sock.length; k++) out[k] = it.sock[k] >= 0 ? it.sock[k] : -1; // 모르는(미래) 코드도 보존 — 박힌 칸으로 취급해 덮어쓰지 않는다
  return out;
}
/** 남은 비약 시간(초, 0 = 꺼짐). play = 지금 플레이 시계 */
export function elixirLeft(s: Pick<GearState, 'elix'>, k: number, play: number): number { return Math.max(0, (s.elix[k] ?? 0) - play); }
/** 소비 아이템 보유 수 */
export function consCount(s: Pick<GearState, 'cons'>, id: string): number { return s.cons[id] ?? 0; }

/** 골드 상점 보호 주문서 가격(마을 상점 단계 배율) */
export function protectPrice(town: number): number {
  const t = Math.max(1, Math.min(SHOP_TIER_PRICE_MULT.length, Math.floor(town)));
  return Math.round(SHOP_PRICE.protect * SHOP_TIER_PRICE_MULT[t - 1]);
}

// ── 저장 형식(dungeonhero.gear.v1) ─────────────────────
const GRADE_IDS = new Set<string>(GRADES.map((g) => g.id));
const OUTCOMES = new Set<string>(['success', 'keep', 'down', 'protected']);
const rec = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const int = (v: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= min && v <= max;
const str = (v: unknown, max = 160): v is string => typeof v === 'string' && v.length > 0 && v.length <= max;

export type ParsedGear = { ok: true; state: GearState } | { ok: false; reason: 'corrupt' | 'future' };

/**
 * D2c 내용(산 물약·보석·비약·박힌 보석)이 있으면 v2로 쓴다: 옛 빌드(v1만 앎)는 v2를 future로 읽어 덮어쓰지 않으므로 이 내용을 지우지 못한다.
 * play(플레이 시계)만 있거나 비약이 모두 만료됐으면(끝 시각 ≤ play) v1로 둔다 — 옛 빌드가 지워도 잃는 것이 거의 없다(한 번 마신 계정이 영구히 v2로 묶이지 않게).
 */
function hasD2cContent(s: GearState): boolean {
  return Object.values(s.cons).some((n) => n > 0) || s.gems.some((n) => n > 0) || s.elix.some((n) => n > s.play) ||
    s.items.some((it) => !!it.sock && it.sock.some((c) => c >= 0));
}

/** 개체 → 저장 형식(affixes는 [id, 값] 쌍). 장부 저장(serializeGear)과 첫 클리어 보상 대기 기록이 같은 형식을 쓴다 */
export function gearItemToJson(it: GearItem): Record<string, unknown> {
  return { uid: it.uid, def: it.def, grade: it.grade, ilvl: it.ilvl, enh: it.enh, pity: it.pity, affixes: it.affixes.map((a) => [a.id, a.v]), ...(it.uniq ? { uniq: it.uniq } : {}), ...(it.nu ? { nu: it.nu } : {}), ...(it.sock && it.sock.some((c) => c >= 0) ? { sock: it.sock } : {}) };
}

/** 저장 문자열(JSON, version 포함). 연산 id·장부는 그대로 남겨 재로드 뒤에도 중복 적용·중복 기록이 없다 */
export function serializeGear(s: GearState): string {
  return JSON.stringify({
    // 숙련·초월이 있으면 v3: 옛 빌드(v2까지)는 future로 읽어 덮어쓰지 않으므로 ms를 지우지 못한다(D2c v2와 같은 규칙)
    version: !hwEmpty(s) ? GEAR_VERSION_HW : hasCompact(s.applied) ? GEAR_VERSION_COMPACT : !masteryEmpty(s.ms) ? GEAR_VERSION : hasD2cContent(s) ? GEAR_VERSION_D2C : GEAR_VERSION_BASE, rev: s.rev, by: s.by,
    items: s.items.map(gearItemToJson),
    equipped: s.equipped, ...(hwEmpty(s) ? {} : { hw: s.hw }), stones: s.stones, protects: s.protects, goldNet: s.goldNet, applied: s.applied, log: s.log,
    protectWeek: s.protectWeek, protectBought: s.protectBought, marks: s.marks,
    // D2c 선택 필드(없으면 빈 값으로 읽는다 — 버전을 올리지 않는다)
    cons: s.cons, gems: s.gems, elix: s.elix, play: s.play,
    ...(masteryEmpty(s.ms) ? {} : { ms: s.ms }),
  });
}

/**
 * 저장 개체 하나 → 검증된 개체(틀리면 null). 장부 저장(parseGear)과 첫 클리어 보상 대기 기록(game/owedReward)이 같은 검사를 쓴다.
 * 고유 효과가 없는 이전 전설은 uid로 결정적으로 정한다(선택 필드라 버전을 올리지 않는다)
 */
export function parseGearItem(v: unknown): GearItem | null {
  if (!rec(v) || !str(v.uid, 128) || !str(v.def) || !GRADE_IDS.has(v.grade as string) || !int(v.ilvl, 1, 999) ||
    !int(v.enh, 0, ENHANCE_MAX) || !Array.isArray(v.pity) || v.pity.length !== ENHANCE_MAX + 1 || !v.pity.every((x) => int(x, 0, 100000)) ||
    !Array.isArray(v.affixes)) return null;
  const affixes: GearAffix[] = [];
  for (const a of v.affixes) {
    if (!Array.isArray(a) || a.length !== 2 || !str(a[0], 40) || typeof a[1] !== 'number' || !Number.isFinite(a[1])) return null;
    affixes.push({ id: a[0] as GearAffix['id'], v: a[1] });
  }
  if (v.uniq !== undefined && !str(v.uniq, 40)) return null;
  // 이름 있는 유니크 표시(레인 B GearItem.nu)는 그대로 보존 — 빠지면 받은 유니크가 효과·거래 가능을 잃는다
  if (v.nu !== undefined && !str(v.nu, 40)) return null;
  if (v.nu !== undefined && !str(v.nu, 40)) return null;
  if (v.sock !== undefined && (!Array.isArray(v.sock) || v.sock.length > 3 || !v.sock.every((c) => int(c, -1, 9999)))) return null;
  return ensureUnique({ uid: v.uid, def: v.def, grade: v.grade as Grade, ilvl: v.ilvl, enh: v.enh, pity: (v.pity as number[]).slice(), affixes, ...(typeof v.uniq === 'string' ? { uniq: v.uniq } : {}), ...(typeof v.nu === 'string' ? { nu: v.nu } : {}), ...(Array.isArray(v.sock) ? { sock: (v.sock as number[]).slice() } : {}) });
}

/**
 * 저장 문자열 → 상태. 구조가 틀리면 corrupt(원문은 저장 계층이 백업), 더 높은 version은 future(덮어쓰지 않는다).
 * 모르는 장비 정의·옵션 id(다음 빌드 콘텐츠)는 버리지 않고 그대로 둔다. 장착이 가방에 없는 uid·다른 부위를 가리키면 그 칸만 비운다.
 */
export function parseGear(raw: string): ParsedGear {
  let p: unknown;
  try { p = JSON.parse(raw); } catch { return { ok: false, reason: 'corrupt' }; }
  if (!rec(p)) return { ok: false, reason: 'corrupt' };
  if (typeof p.version === 'number' && p.version > GEAR_VERSION_HW) return { ok: false, reason: 'future' };
  if (p.version !== GEAR_VERSION_BASE && p.version !== GEAR_VERSION_D2C && p.version !== GEAR_VERSION && p.version !== GEAR_VERSION_COMPACT && p.version !== GEAR_VERSION_HW) return { ok: false, reason: 'corrupt' };
  if (!int(p.rev) || typeof p.by !== 'string' || !Array.isArray(p.items) || !rec(p.equipped) || !Array.isArray(p.stones) || p.stones.length !== 4 ||
    !int(p.protects) || !int(p.goldNet, Number.MIN_SAFE_INTEGER) || !Array.isArray(p.applied) || !Array.isArray(p.log) ||
    !int(p.protectWeek, -1) || !int(p.protectBought)) return { ok: false, reason: 'corrupt' };
  const s = freshGear();
  s.rev = p.rev; s.by = p.by; s.protects = p.protects; s.goldNet = p.goldNet; s.protectWeek = p.protectWeek; s.protectBought = p.protectBought;
  for (let i = 0; i < 4; i++) { if (!int(p.stones[i])) return { ok: false, reason: 'corrupt' }; s.stones[i] = p.stones[i] as number; }
  const uids = new Set<string>();
  for (const v of p.items) {
    const item = parseGearItem(v);
    if (!item || uids.has(item.uid)) return { ok: false, reason: 'corrupt' };
    uids.add(item.uid);
    s.items.push(item);
  }
  for (const k of SLOT_ORDER) {
    const u = p.equipped[k];
    if (u !== null && u !== undefined && typeof u !== 'string') return { ok: false, reason: 'corrupt' };
    const it = typeof u === 'string' ? s.items.find((x) => x.uid === u) : undefined;
    s.equipped[k] = it && itemSlot(it) === k ? it.uid : null;
  }
  // 영웅별 무기 칸(v5). 칸이 가방에 없는 uid·무기가 아닌 장비·다른 영웅 전용·다른 칸과 겹치면 그 칸만 비운다(equipped와 같은 규칙)
  if (p.hw !== undefined) {
    if (!rec(p.hw)) return { ok: false, reason: 'corrupt' };
    for (const [h, u] of Object.entries(p.hw)) {
      if (typeof u !== 'string') return { ok: false, reason: 'corrupt' };
      if (!HW_SET.has(h)) continue;
      const it = s.items.find((x) => x.uid === u);
      const lock = it ? equipDef(it.def)?.heroLock : undefined;
      if (!it || itemSlot(it) !== 'weapon' || (lock && lock !== h) || isEquipped(s, u)) continue;
      s.hw[h as HeroId] = u;
    }
  }
  for (const id of p.applied) { if (!str(id)) return { ok: false, reason: 'corrupt' }; s.applied.push(id); }
  trimApplied(s.applied);
  for (const l of p.log) {
    if (!rec(l) || !str(l.op) || !str(l.uid, 128) || !int(l.from, 0, ENHANCE_MAX) || !int(l.to, 0, ENHANCE_MAX) || !OUTCOMES.has(l.outcome as string) ||
      typeof l.chance !== 'number' || typeof l.protect !== 'boolean' || !int(l.stones) || !int(l.gold)) return { ok: false, reason: 'corrupt' };
    s.log.push({ op: l.op, uid: l.uid, from: l.from, to: l.to, outcome: l.outcome as EnhanceOutcome, chance: l.chance, protect: l.protect, stones: l.stones, gold: l.gold });
  }
  // 수위표(없으면 빈 표: 이 필드 전 v1 개발 저장도 그대로 읽는다)
  if (p.marks !== undefined) {
    if (!Array.isArray(p.marks)) return { ok: false, reason: 'corrupt' };
    for (const m of p.marks) {
      if (!Array.isArray(m) || m.length !== 2 || !str(m[0]) || !int(m[1])) return { ok: false, reason: 'corrupt' };
      if (s.marks.length < MARKS_CAP && !s.marks.some((x) => x[0] === m[0])) s.marks.push([m[0] as string, m[1] as number]);
    }
  }
  // D2c 소비 아이템·보석·비약·플레이 시계(없으면 빈 값: 이 필드 전 v1 저장도 그대로 읽는다)
  if (p.cons !== undefined) {
    if (!rec(p.cons)) return { ok: false, reason: 'corrupt' };
    for (const [k, n] of Object.entries(p.cons)) { if (!str(k, 40) || !int(n, 0, STACK_CAP)) return { ok: false, reason: 'corrupt' }; if (n > 0) s.cons[k] = n; }
  }
  if (p.gems !== undefined) {
    // 짧으면 0으로 채우고, 길면(다음 빌드의 새 보석) 꼬리를 그대로 보존한다. 효과·박기는 아는 코드만 본다
    if (!Array.isArray(p.gems) || p.gems.length > COUNT_ARRAY_MAX || !p.gems.every((n) => int(n))) return { ok: false, reason: 'corrupt' };
    s.gems = (p.gems as number[]).slice();
    while (s.gems.length < GEM_CODES) s.gems.push(0);
  }
  if (p.elix !== undefined) {
    if (!Array.isArray(p.elix) || p.elix.length > COUNT_ARRAY_MAX || !p.elix.every((n) => int(n))) return { ok: false, reason: 'corrupt' };
    s.elix = (p.elix as number[]).slice();
    while (s.elix.length < ELIXIRS.length) s.elix.push(0);
  }
  if (p.play !== undefined) { if (!int(p.play)) return { ok: false, reason: 'corrupt' }; s.play = p.play; }
  // 숙련·초월(C4, 없으면 빈 값: v1·v2 저장도 그대로 읽는다)
  if (p.ms !== undefined) { const m = parseMastery(p.ms); if (!m) return { ok: false, reason: 'corrupt' }; s.ms = m; }
  return { ok: true, state: s };
}
